import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Button from "components/Button";
import DatePickerField from "components/DatePickerField";
import FormField from "components/FormField";
import { endOfMonth } from "date-fns";
import { useUserState } from "context";

const ResyncSchema = Yup.object().shape({
  dateFrom: Yup.date().required("Date From is required").nullable(),
  detectOrphans: Yup.boolean(),
  limitOrphansToDate: Yup.boolean(),
  organizationId: Yup.number()
    .integer("Must be a valid organization ID")
    .positive("Must be a valid organization ID")
    .nullable()
    .typeError("Must be a valid organization ID"),
});

const ResyncAdminSchema = ResyncSchema.shape({
  organizationId: Yup.number()
    .integer("Must be a valid organization ID")
    .positive("Must be a valid organization ID")
    .required("Organization ID is required")
    .typeError("Must be a valid organization ID"),
});

const ResyncForm = ({ onSubmit, isLoading, isProcessing }) => {
  const { isAdmin } = useUserState();

  return (
    <Formik
      initialValues={{
        dateFrom: null,
        detectOrphans: false,
        limitOrphansToDate: false,
        organizationId: "",
      }}
      validationSchema={isAdmin ? ResyncAdminSchema : ResyncSchema}
      onSubmit={onSubmit}
    >
      {({ isValid, dirty, values, setFieldValue }) => (
        <Form className="flex flex-col gap-6 w-full">
          <div className="flex items-end gap-6 w-full">
            <div className="flex-1">
              <DatePickerField
                label="Date From (Month/Year)"
                name="dateFrom"
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                placeholderText="Select month and year"
                maxDate={endOfMonth(new Date())}
                disabled={isProcessing}
                required
                containerClassName="w-full"
                className="w-full"
              />
            </div>
            <div className="mb-1 flex-shrink-0">
              <Button
                type="submit"
                disabled={!isValid || !dirty || isProcessing}
                isLoading={isLoading}
                loadingText="Starting..."
              >
                Start Resync
              </Button>
            </div>
          </div>

          {isAdmin && (
            <div className="w-48">
              <FormField
                name="organizationId"
                label="Organization ID"
                type="number"
                placeholder="e.g. 7716"
                disabled={isProcessing}
              />
            </div>
          )}

          {isAdmin && (
            <div className="flex flex-col gap-3 p-4 bg-amber-50 border border-amber-200 rounded">
              <div className="flex items-start gap-3">
                <input
                  id="detectOrphans"
                  type="checkbox"
                  checked={values.detectOrphans}
                  onChange={(e) => {
                    setFieldValue("detectOrphans", e.target.checked);
                    if (!e.target.checked) {
                      setFieldValue("limitOrphansToDate", false);
                    }
                  }}
                  disabled={isProcessing}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 cursor-pointer"
                />
                <div>
                  <label
                    htmlFor="detectOrphans"
                    className="text-sm font-medium text-amber-900 cursor-pointer"
                  >
                    Detect orphaned animals
                  </label>
                  <p className="text-xs text-amber-700 mt-0.5">
                    After resyncing, identify animals no longer returned by the
                    ShelterLuv API and flag them for cleanup. Use this when
                    remediating historical data discrepancies.
                  </p>
                </div>
              </div>

              {values.detectOrphans && (
                <div className="flex items-start gap-3 pl-7">
                  <input
                    id="limitOrphansToDate"
                    type="checkbox"
                    checked={values.limitOrphansToDate}
                    onChange={(e) =>
                      setFieldValue("limitOrphansToDate", e.target.checked)
                    }
                    disabled={isProcessing}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 cursor-pointer"
                  />
                  <div>
                    <label
                      htmlFor="limitOrphansToDate"
                      className="text-sm font-medium text-amber-900 cursor-pointer"
                    >
                      Limit orphan detection to selected date range
                    </label>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Only check animals added to the database from the selected
                      date onward. Recommended for large organizations to avoid
                      timeouts.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default ResyncForm;
